import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CanAccess, useDataProvider, useTranslate } from "ra-core";
import { Navigate } from "react-router";

import { Button } from "@/components/ui/button";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import { InboxRow } from "./InboxRow";
import { UnclassifiedNavLinks } from "./UnclassifiedNavLinks";
import { WhatsAppCounterCard } from "./WhatsAppCounterCard";

const PER_PAGE = 25;

const InboxList = ({ unmatched }: { unmatched: boolean }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [page, setPage] = useState(1);
  const inbox = useQuery({
    queryKey: ["whatsapp", "inbox", unmatched, page],
    queryFn: () =>
      dataProvider.getWhatsAppInbox({ unmatched, page, perPage: PER_PAGE }),
    retry: false,
  });

  if (inbox.error) {
    return (
      <ListErrorState
        message={translate("crm.whatsapp.inbox.error")}
        onRetry={() => inbox.refetch()}
      />
    );
  }
  if (!inbox.data) return <ListCardsSkeleton rows={3} />;
  if (inbox.data.rows.length === 0 && page === 1) {
    return (
      <ListEmptyState
        title={translate(
          unmatched
            ? "crm.whatsapp.inbox.empty_unmatched"
            : "crm.whatsapp.inbox.empty",
        )}
      />
    );
  }

  const pages = Math.max(1, Math.ceil(inbox.data.total / PER_PAGE));
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {inbox.data.rows.map((row) => (
          <li key={String(row.conversationId)}>
            <InboxRow row={row} />
          </li>
        ))}
      </ul>
      {pages > 1 ? (
        <nav
          className="flex items-center justify-between gap-2"
          aria-label={translate("crm.whatsapp.inbox.pagination")}
        >
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {translate("crm.whatsapp.inbox.previous")}
          </Button>
          <span className="text-[15px] text-muted-foreground">
            {translate("crm.whatsapp.inbox.page_of", { page, pages })}
          </span>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            {translate("crm.whatsapp.inbox.next")}
          </Button>
        </nav>
      ) : null}
    </div>
  );
};

const InboxContent = () => {
  const translate = useTranslate();
  return (
    <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
      <h1 className="px-1 text-2xl font-bold">
        {translate("crm.whatsapp.inbox.title")}
      </h1>
      <p className="px-1 text-[15px] text-muted-foreground">
        {translate("crm.whatsapp.inbox.intro")}
      </p>
      {/* Direccion only: the card renders nothing for any other role. */}
      <WhatsAppCounterCard compact />
      {/* Almikia Epic EW3: links to the 'Sin clasificar' tray, only for the permission holder. */}
      <UnclassifiedNavLinks />
      <section className="space-y-3" aria-labelledby="whatsapp-unmatched">
        <h2 id="whatsapp-unmatched" className="px-1 text-lg font-semibold">
          {translate("crm.whatsapp.inbox.unmatched_title")}
        </h2>
        <InboxList unmatched />
      </section>
      <section className="space-y-3" aria-labelledby="whatsapp-conversations">
        <h2 id="whatsapp-conversations" className="px-1 text-lg font-semibold">
          {translate("crm.whatsapp.inbox.conversations_title")}
        </h2>
        <InboxList unmatched={false} />
      </section>
    </div>
  );
};

/**
 * Almikia Epic EW2 (TASK-080): the WhatsApp 'Bandeja'. Direccion and lead_manager (within her
 * scope: RLS decides the rows) see the conversations with unread counts, the 'Responde' badge,
 * the window state and the last message, plus the conversations that have no lead yet. An
 * asesora has no entry here: she sees the badges on her own leads (kanban, prospect list, Hoy)
 * and opens their conversation on the lead file. The alerts number never appears: its traffic
 * is not stored in the conversation tables.
 */
export const InboxScreen = () => (
  <CanAccess
    resource="whatsapp_inbox"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <InboxContent />
  </CanAccess>
);

InboxScreen.path = "/whatsapp_inbox";
